//
// QnsDomainsTooltipContent
//
// Renders inside the AddressEntity tooltip when the address owns more than
// one .qie domain. Lists all of them with a paid/free tier badge so users
// can see the full picture without leaving the page.
//
// Capped at 10 visible rows (with a "+N more" footer) so an address with
// dozens of free domains doesn't blow the tooltip up. Sorted with paid
// first, then alphabetically.
//
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';

import type { QnsDomain } from 'lib/api/useQnsResolution';

const MAX_VISIBLE_DOMAINS = 10;

interface Props {
  address: string;
  primaryDomain: string | null;
  allDomains: Array<QnsDomain>;
  totalDomains: number;
}

const QnsDomainsTooltipContent = ({ primaryDomain, allDomains, totalDomains }: Props) => {
  const sortedDomains = React.useMemo(() => {
    return [ ...allDomains ].sort((a, b) => {
      const aPaid = a.domainType === 'paid' ? 0 : 1;
      const bPaid = b.domainType === 'paid' ? 0 : 1;
      if (aPaid !== bPaid) return aPaid - bPaid;
      return a.domainName.localeCompare(b.domainName);
    });
  }, [ allDomains ]);

  const visible = sortedDomains.slice(0, MAX_VISIBLE_DOMAINS);
  const hiddenCount = Math.max(0, sortedDomains.length - MAX_VISIBLE_DOMAINS);

  return (
    <Box py={ 1 } maxW="280px">
      <Text fontSize="xs" fontWeight={ 600 } opacity={ 0.85 } mb={ 2 }>
        Owns { totalDomains } .qie domain{ totalDomains === 1 ? '' : 's' }
      </Text>
      { visible.map((d) => {
        const isPrimary = d.domainName === primaryDomain;
        return (
          <Flex
            key={ d.domainName }
            alignItems="center"
            justifyContent="space-between"
            gap={ 2 }
            py={ 0.5 }
          >
            <Text
              fontSize="xs"
              fontWeight={ isPrimary ? 700 : 400 }
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              { d.domainName }
            </Text>
            <Badge
              colorScheme={ d.domainType === 'paid' ? 'green' : 'gray' }
              textTransform="capitalize"
              fontSize="2xs"
              flexShrink={ 0 }
            >
              { d.domainType }
            </Badge>
          </Flex>
        );
      }) }
      { hiddenCount > 0 && (
        <Text fontSize="xs" opacity={ 0.7 } mt={ 1 }>
          + { hiddenCount } more
        </Text>
      ) }
    </Box>
  );
};

export default React.memo(QnsDomainsTooltipContent);